import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { apiJson } from '../../lib/api'
import { zoneOptions } from '../../lib/timezones'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { ClientsResponse, LegacyPackhouse, LegacyPackhousesResponse, licenceText } from './api'
import styles from './GodMode.module.css'

// A new client: its name, product, licence (PackRight only), time zone,
// region and, for a client moving from the old system, its legacy packhouse. The support seed gives it the three Dataphyll sign-ins, so it opens
// straight away.
export function NewClientPage() {
  const { user } = useAuth()
  const { reloadWorkspaces } = useWorkspace()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [meta, setMeta] = useState<ClientsResponse | null>(null)
  const [packhouses, setPackhouses] = useState<LegacyPackhouse[]>([])
  const [legacy, setLegacy] = useState(params.get('legacy') || '')
  const [name, setName] = useState('')
  const [product, setProduct] = useState('')
  const [licence, setLicence] = useState('')
  const [timezone, setTimezone] = useState('Pacific/Auckland')
  const [region, setRegion] = useState('NZ')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const regions = meta?.regions.length ? meta.regions : [{ code: 'NZ', label: 'New Zealand', time_zones: ['Pacific/Auckland'] }]
  const regionZones = regions.find((r) => r.code === region)?.time_zones ?? []
  // A region offers only its own zones, its usual one first; one that lists
  // none offers every zone.
  const zones = useMemo(() => (regionZones.length ? regionZones : zoneOptions(timezone)), [regionZones.join(), timezone])
  const pickRegion = (code: string) => {
    setRegion(code)
    const z = regions.find((r) => r.code === code)?.time_zones ?? []
    if (z.length && !z.includes(timezone)) setTimezone(z[0])
  }

  useEffect(() => {
    let live = true
    apiJson<ClientsResponse>(user, '/api/god-mode/clients')
      .then((d) => live && setMeta(d))
      .catch((e) => live && setError(e.message))
    apiJson<LegacyPackhousesResponse>(user, '/api/god-mode/legacy-packhouses')
      .then((d) => {
        if (!live) return
        setPackhouses(d.packhouses)
        const p = d.packhouses.find((x) => String(x.packhouse_id) === params.get('legacy'))
        if (p) setName((n) => n || p.name)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [user, params])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<{ client: { id: string } }>(user, '/api/god-mode/clients', {
        method: 'POST',
        body: {
          name,
          product,
          licence: product === 'packright' && licence ? licence : null,
          timezone,
          region,
          ...(legacy ? { legacy_packhouse_id: Number(legacy) } : {}),
        },
      })
      reloadWorkspaces()
      navigate(`/god-mode/clients/${r.client.id}`)
    } catch (err: any) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title="New client" />
      <PageBody>
        <form className={`${styles.card} ${styles.form}`} onSubmit={submit}>
          {error && <p className={styles.error}>{error}</p>}
          <label className={styles.field}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoFocus />
          </label>
          <label className={styles.field}>
            Product
            <select value={product} onChange={(e) => setProduct(e.target.value)} required>
              <option value="">Choose a product</option>
              {meta?.products.map((p) => (
                <option key={p.k} value={p.k}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {product === 'packright' && (
            <label className={styles.field}>
              Stock &amp; Dispatch licence
              <select value={licence} onChange={(e) => setLicence(e.target.value)}>
                <option value="">None</option>
                {meta?.licences.map((l) => (
                  <option key={l} value={l}>
                    {licenceText(l)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={styles.field}>
            Region
            <select value={region} onChange={(e) => pickRegion(e.target.value)}>
              {regions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
            <span className={styles.hint}>Sets the client's break rules and the time zones offered.</span>
          </label>
          <label className={styles.field}>
            Time zone
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} required>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
            <span className={styles.hint}>A client across several zones sets each site's own on its Sites page.</span>
          </label>
          <label className={styles.field}>
            Legacy packhouse
            <select value={legacy} onChange={(e) => setLegacy(e.target.value)}>
              <option value="">None: a new client</option>
              {packhouses
                .filter((p) => p.packhouse_id >= 0 && !p.client)
                .map((p) => (
                  <option key={p.packhouse_id} value={p.packhouse_id}>
                    {p.packhouse_id} {p.name}
                  </option>
                ))}
            </select>
            <span className={styles.hint}>A client moving from the old system keeps its packhouse ID, so its tags stay its own.</span>
          </label>
          <div>
            <button type="submit" className={styles.primary} disabled={busy || !name.trim() || !product}>
              {busy ? 'Creating…' : 'Create client'}
            </button>
          </div>
        </form>
      </PageBody>
    </>
  )
}
